import { createSlice } from '@reduxjs/toolkit';

import { Appointment, UnallocatedAppointment } from '~weekly-planner/lib/common';

import { typePrefix } from '~weekly-planner/actions/simulate';

export interface UnsavedChanges {
  [id: number]: {
    original: UnallocatedAppointment | Appointment;
    change: Change;
  };
}

export interface Change {
  id: number | null;
  user_id: number;
  date?: string;
  parent_repeat_id?: number;
  start_time?: string;
  end_time?: string;
  notes_admin?: string;
}

interface State {
  appointments: UnsavedChanges;
  interacting: number | null;
}

const initialState: State = {
  appointments: {},
  interacting: null,
};

const isChangeReverted = (original: Appointment, change: Change): boolean => {
  if ((change.user_id ?? null) !== (original.user_id ?? null)) {
    return false;
  }

  const fields = ['start_time', 'end_time', 'date'] as const;

  for (const field of fields) {
    if (field in change) {
      if (change[field] !== original[field]) {
        return false;
      }
    }
  }

  return true;
};

const slice = createSlice({
  name: `${typePrefix}`,
  initialState,
  reducers: {
    setInteracting(state, action) {
      state.interacting = parseInt(action.payload);
    },
    clearInteracting(state) {
      state.interacting = null;
    },
    clear(state) {
      state.appointments = {};
    },
    makeChange(state, action) {
      const { original, change } = action.payload;
      const key = change.id ?? change.parent_repeat_id;

      if (key == null) return;

      const existing = state.appointments[key];

      if (existing) {
        // Merge the new change with the existing one
        state.appointments[key].change = {
          ...state.appointments[key].change,
          ...change,
        };
        // Compare the new change against the stored original snapshot
        if (isChangeReverted(existing.original, state.appointments[key].change)) {
          delete state.appointments[key];
        }
      } else {
        // First time changing - snapshot the current appointment as the original
        state.appointments[key] = {
          original,
          change,
        };
      }
    },
  },
});

export const { setInteracting, clearInteracting, makeChange, clear } = slice.actions;

export default slice.reducer;
